<template>
  <Map ref="googleMapRef"
       class="map"
       mapTypeId="terrain"
       :use-map-id="true"
       :zoom="mapZoom"
       scrollwheel
       :center="center"
       @on-map-ready="onMapReady">
    <CustomControl position="TOP_LEFT" :index="3" class="mb-5">
      <div class="suppliers-map-date">
        <Datepicker
            v-model="selectedDate"
            :format="$t('settings.generic.date-format')"
            :locale="locale"
            :clearable="false"
            :enable-time-picker="false"
            auto-apply
        />
      </div>
    </CustomControl>
    <CustomControl position="LEFT_TOP" :index="1" class="mb-5">
      <button class="custom-btn" @click="isMenuOpened = !isMenuOpened">
        {{ $t("menu") }}
      </button>
    </CustomControl>
    <CustomControl position="LEFT_TOP" :index="2" class="mb-5">
      <button class="custom-btn" @click="syncOazisDaily">
        {{ $t('task.oazis.sync.for-date', {date: selectedDateLabel}) }}
      </button>
    </CustomControl>
    <CustomControl position="LEFT_TOP" :index="4">
      <div
          ref="legend"
          class="suppliers-map-legend"
          @wheel.stop
          @touchmove.stop>
        <div class="font-size-15 font-bold">{{ $t('oazis-suppliers-map.legend') }}</div>
          <label class="suppliers-map-legend__item suppliers-map-legend__select-all">
            <span class="suppliers-map-legend__checkbox-wrapper">
              <input
                  ref="selectAllTasksCheckbox"
                  type="checkbox"
                  class="suppliers-map-legend__checkbox"
                  :aria-checked="isAllVisibleDriversIndeterminate ? 'mixed' : areAllVisibleDriversChecked"
                  :checked="areAllVisibleDriversChecked"
                  v-indeterminate="isAllVisibleDriversIndeterminate"
                  @change="toggleAllTasks($event.target.checked)" />
              <span
                  v-if="isAllVisibleDriversIndeterminate"
                  class="suppliers-map-legend__checkbox-indeterminate"
                  aria-hidden="true"></span>
            </span>
            <span class="suppliers-map-legend__select-all-label">{{ $t('oazis-suppliers-map.all-orders') }}</span>
          </label>
          <label class="suppliers-map-legend__item suppliers-map-legend__select-all">
            <span class="suppliers-map-legend__checkbox-wrapper">
              <input
                  ref="selectAllRequestsCheckbox"
                  type="checkbox"
                  class="suppliers-map-legend__checkbox"
                  :aria-checked="isAllVisibleRequestsIndeterminate ? 'mixed' : areAllVisibleRequestsChecked"
                  :checked="areAllVisibleRequestsChecked"
                  v-indeterminate="isAllVisibleRequestsIndeterminate"
                  @change="toggleAllRequests($event.target.checked)" />
              <span
                  v-if="isAllVisibleRequestsIndeterminate"
                  class="suppliers-map-legend__checkbox-indeterminate"
                  aria-hidden="true"></span>
            </span>
            <span class="suppliers-map-legend__select-all-label">{{ $t('oazis-suppliers-map.all-requests') }}</span>
          </label>
          <button
              type="button"
              class="suppliers-map-legend__item suppliers-map-legend__accordion-toggle"
              :aria-expanded="isTasksAccordionOpen"
              @click="isTasksAccordionOpen = !isTasksAccordionOpen">
            <span>{{ $t('oazis-suppliers-map.orders') }}</span>
            <span aria-hidden="true">{{ isTasksAccordionOpen ? '▾' : '▸' }}</span>
          </button>
          <template v-if="isTasksAccordionOpen">
            <label v-for="driver in driverLegend" :key="driver.key" class="suppliers-map-legend__item suppliers-map-legend__driver-toggle">
              <input
                  type="checkbox"
                  class="suppliers-map-legend__checkbox"
                  :checked="isTaskChecked(driver.key)"
                  @change="toggleTask(driver.key, $event.target.checked)" />
              <span class="suppliers-map-legend__swatch" :style="{backgroundColor: driver.color}"></span>
              <span class="suppliers-map-legend__text">
                <span class="suppliers-map-legend__driver">{{ driver.name }}</span>
                <span class="suppliers-map-legend__vehicle">{{ driver.vehicleName }}</span>
              </span>
            </label>
          </template>
          <button
              type="button"
              class="suppliers-map-legend__item suppliers-map-legend__accordion-toggle"
              :aria-expanded="isRequestsAccordionOpen"
              @click="isRequestsAccordionOpen = !isRequestsAccordionOpen">
            <span>{{ $t('oazis-suppliers-map.requests') }}</span>
            <span aria-hidden="true">{{ isRequestsAccordionOpen ? '▾' : '▸' }}</span>
          </button>
          <template v-if="isRequestsAccordionOpen">
            <label v-for="request in requestLegend" :key="request.key" class="suppliers-map-legend__item suppliers-map-legend__driver-toggle">
              <input
                  type="checkbox"
                  class="suppliers-map-legend__checkbox"
                  :checked="isRequestChecked(request.key)"
                  @change="toggleRequest(request.key, $event.target.checked)" />
              <span class="suppliers-map-legend__swatch" :style="{backgroundColor: request.color}"></span>
              <span class="suppliers-map-legend__text">
                <span class="suppliers-map-legend__driver">{{ request.name }}</span>
              </span>
            </label>
          </template>
      </div>
    </CustomControl>
    <AdvancedMarker v-for="marker in filteredGeoFenceMarkers"
                    :key="marker.id"
                    :options="marker.options"
                    :pin-options="marker.pinOptions"
                    @click="selectMarker(marker)" />
    <InfoWindow v-if="selectedMarker"
                :options="selectedMarkerInfoWindowOptions"
                @closeclick="selectedMarker = null">
      <div class="suppliers-map-popup">
        <GeoFenceInfo
            :title="selectedMarker.geoFence.name"
            :description="selectedMarker.geoFence.description"
            :coordinates="selectedMarker.geoFence.area.coordinates"
            :radius="selectedMarker.geoFence.area.radius"
        />
      </div>
    </InfoWindow>
  </Map>
  <VueSidePanel
      v-model="isMenuOpened"
      lock-scroll
      :overlay-opacity="0"
      side="left">
    <div class="pt-10">
      <NavBar :support-mobile="false" />
    </div>
  </VueSidePanel>
</template>

<script>
import {AdvancedMarker, CustomControl, InfoWindow} from "vue3-google-map";
import {VueSidePanel} from "vue3-side-panel";
import Datepicker from "@vuepic/vue-datepicker";
import moment from "moment/moment";
import {mapGetters, mapState} from "vuex";
import Map from "@/components/pages/partials/maps/Map.vue";
import NavBar from "@/components/NavBar.vue";
import GeoFenceInfo from "@/components/pages/geo-fences/GeoFenceInfo.vue";
import {dataManipulator, entityDataManager, GoogleMapsManager} from "@/assets/js/data-manipulation";
import {TaskTagType} from "@/assets/js/enums/TaskTagType.ts";
import {actions} from "@/assets/js/vue/vuex/actions";
import notification from "@/assets/js/notification";
import locale from "@/assets/js/locale";

const legendPalette = ['#D92D20', '#1570EF', '#039855', '#DC6803', '#7A5AF8', '#C11574', '#0E9384', '#475467', '#B54708', '#175CD3'];

export default {
  name: "OazisSuppliersMapPage",
  components: {GeoFenceInfo, NavBar, Datepicker, VueSidePanel, InfoWindow, AdvancedMarker, CustomControl, Map},
  directives: {
    indeterminate: {
      mounted(element, binding) {
        element.indeterminate = binding.value;
      },
      updated(element, binding) {
        element.indeterminate = binding.value;
      },
    },
  },
  props: {
    searchToken: String,
  },
  data() {
    return {
      center: GoogleMapsManager.defaultCenter,
      mapZoom: 12,
      selectedDate: moment().startOf('day').toDate(),
      locale: locale.getLocale(),
      tasks: [],
      requests: [],
      isMapReady: false,
      isMenuOpened: false,
      selectedMarker: null,
      hiddenDriverKeys: [],
      hiddenRequestKeys: [],
      isTasksAccordionOpen: true,
      isRequestsAccordionOpen: true,
    };
  },
  computed: {
    ...mapState(['drivers', 'vehicles']),
    ...mapGetters(['allGeoFences']),
    selectedDateLabel() {
      return moment(this.selectedDate).format(this.$t('settings.generic.date-format-moment'));
    },
    allDriverLegend() {
      const driversByKey = {};

      this.tasks.forEach(task => {
        const key = this.getTaskDriverKey(task);
        const driver = driversByKey[key] ?? (driversByKey[key] = {
          key: key,
          name: this.getTaskDriverName(task),
          vehicleNames: new Set(),
        });
        driver.vehicleNames.add(this.getTaskVehicleName(task));
      });

      return Object.values(driversByKey)
          .sort((first, second) => first.name.localeCompare(second.name))
          .map((driver, index) => ({
            key: driver.key,
            name: driver.name,
            vehicleName: Array.from(driver.vehicleNames).join(', ') || '-',
            color: this.getLegendColor(index),
          }));
    },
    driverLegend() {
      return this.allDriverLegend.filter(driver => this.legendMatchesSearch(driver));
    },
    driverColorsByKey() {
      return this.allDriverLegend.reduce((acc, driver) => {
        acc[driver.key] = driver.color;
        return acc;
      }, {});
    },
    allRequestLegend() {
      const requestsByKey = {};

      this.requests.forEach(request => {
        const key = this.getRequestKey(request);
        if (!requestsByKey[key]) {
          requestsByKey[key] = {
            key,
            name: request.administrationGroup,
          };
        }
      });

      return Object.values(requestsByKey)
          .sort((first, second) => first.name.localeCompare(second.name))
          .map((request, index) => ({
            ...request,
            color: this.getLegendColor(this.allDriverLegend.length + index),
          }));
    },
    requestLegend() {
      return this.allRequestLegend.filter(request => this.legendMatchesSearch(request));
    },
    requestColorsByKey() {
      return this.allRequestLegend.reduce((acc, request) => {
        acc[request.key] = request.color;
        return acc;
      }, {});
    },
    activeDriverKeys() {
      return new Set(this.driverLegend
          .filter(driver => this.isTaskChecked(driver.key))
          .map(driver => driver.key));
    },
    visibleDriverKeys() {
      return this.driverLegend.map(driver => driver.key);
    },
    checkedVisibleDriverCount() {
      return this.visibleDriverKeys.filter(driverKey => this.isTaskChecked(driverKey)).length;
    },
    areAllVisibleDriversChecked() {
      return this.visibleDriverKeys.length > 0 && this.checkedVisibleDriverCount === this.visibleDriverKeys.length;
    },
    isAllVisibleDriversIndeterminate() {
      return this.checkedVisibleDriverCount > 0 && this.checkedVisibleDriverCount < this.visibleDriverKeys.length;
    },
    activeRequestKeys() {
      return new Set(this.requestLegend
          .filter(request => this.isRequestChecked(request.key))
          .map(request => request.key));
    },
    visibleRequestKeys() {
      return this.requestLegend.map(request => request.key);
    },
    checkedVisibleRequestCount() {
      return this.visibleRequestKeys.filter(requestKey => this.isRequestChecked(requestKey)).length;
    },
    areAllVisibleRequestsChecked() {
      return this.visibleRequestKeys.length > 0 && this.checkedVisibleRequestCount === this.visibleRequestKeys.length;
    },
    isAllVisibleRequestsIndeterminate() {
      return this.checkedVisibleRequestCount > 0 && this.checkedVisibleRequestCount < this.visibleRequestKeys.length;
    },
    normalizedSearchToken() {
      return (this.searchToken ?? '').trim().toLowerCase();
    },
    geoFenceMarkers() {
      const markers = [...this.createBaseTaskGeoFenceMarkers(), ...this.createBaseRequestGeoFenceMarkers()];
      const coordinateCounts = markers.reduce((acc, marker) => {
        const key = this.getCoordinateKey(marker.basePosition);
        acc[key] = (acc[key] ?? 0) + 1;
        return acc;
      }, {});
      const coordinateIndexes = {};

      return markers.map(marker => {
        const coordinateKey = this.getCoordinateKey(marker.basePosition);
        const coordinateIndex = coordinateIndexes[coordinateKey] ?? 0;
        coordinateIndexes[coordinateKey] = coordinateIndex + 1;

        const position = this.offsetPosition(marker.basePosition, coordinateIndex, coordinateCounts[coordinateKey]);
        return {
          ...marker,
          position,
          options: {
            position,
            title: `${marker.legendName} - ${marker.geoFence.name}`,
          },
          pinOptions: this.createPinOptions(marker.color),
        };
      });
    },
    filteredGeoFenceMarkers() {
      return this.geoFenceMarkers.filter(marker => marker.type === 'task'
          ? this.activeDriverKeys.has(marker.legendKey)
          : this.activeRequestKeys.has(marker.legendKey));
    },
    selectedMarkerInfoWindowOptions() {
      if (!this.selectedMarker) {
        return {};
      }

      return {
        position: this.selectedMarker.position,
        headerContent: this.createGeoFenceInfoWindowHeaderContent(this.selectedMarker.legendName),
      };
    },
  },
  watch: {
    selectedDate() {
      this.loadTasks();
    },
    searchToken() {
      this.closeSelectedMarkerIfHidden();
      this.updateSelectAllCheckboxState();
    },
    driverLegend() {
      this.updateSelectAllCheckboxState();
    },
    requestLegend() {
      this.updateSelectAllCheckboxState();
    },
    hiddenDriverKeys() {
      this.updateSelectAllCheckboxState();
    },
    hiddenRequestKeys() {
      this.updateSelectAllCheckboxState();
    },
  },
  mounted() {
    this.loadTasks();
    this.updateSelectAllCheckboxState();
  },
  methods: {
    onMapReady() {
      this.isMapReady = true;
      this.fitMapToMarkers();
    },
    async loadTasks() {
      this.selectedMarker = null;

      try {
        const [tasks, requests] = await Promise.all([
          this.$store.dispatch(actions.GET_TASKS, {
            dateFrom: moment(this.selectedDate).startOf('day').toDate(),
            dateTo: moment(this.selectedDate).endOf('day').toDate(),
            tags: [TaskTagType.OazisIntegration],
            saveInStore: false,
          }),
          this.$store.dispatch(actions.GET_OAZIS_REQUESTS, {
            date: dataManipulator.dateToString(this.selectedDate),
          }),
        ]);
        this.tasks = tasks;
        this.requests = requests;
        this.$nextTick(() => {
          this.fitMapToMarkers();
        });
      } catch (e) {
        notification.error(this.$t(e.message));
      }
    },
    async syncOazisDaily() {
      await this.$store.dispatch(actions.SYNC_OAZIS_DAILY, {
        date: dataManipulator.dateToString(this.selectedDate),
      })
          .then(() => {
            notification.success(this.$t('task.oazis.sync.success'));
            return this.loadTasks();
          })
          .catch(e => notification.error(this.$t(e.message)));
    },
    createBaseTaskGeoFenceMarkers() {
      return this.tasks.reduce((markers, task) => {
        const driverKey = this.getTaskDriverKey(task);
        const color = this.driverColorsByKey[driverKey] ?? legendPalette[0];
        const driverName = this.getTaskDriverName(task);

        (task.taskGeofences ?? []).forEach((taskGeoFence, index) => {
          const geoFence = entityDataManager.getById(this.allGeoFences, taskGeoFence.geofenceId);
          const basePosition = geoFence?.area?.coordinates;
          if (!basePosition) {
            return;
          }

          markers.push({
            id: `${task.id}-${taskGeoFence.geofenceId}-${index}`,
            task,
            geoFence,
            type: 'task',
            legendKey: driverKey,
            legendName: driverName,
            basePosition,
            color,
          });
        });

        return markers;
      }, []);
    },
    createBaseRequestGeoFenceMarkers() {
      return this.requests.reduce((markers, request) => {
        const requestKey = this.getRequestKey(request);
        const color = this.requestColorsByKey[requestKey] ?? legendPalette[0];

        (request.geofences ?? []).forEach((requestGeofence, index) => {
          const geoFence = entityDataManager.getById(this.allGeoFences, requestGeofence.id);
          const basePosition = geoFence?.area?.coordinates;
          if (!basePosition) {
            return;
          }

          markers.push({
            id: `request-${request.id}-${requestGeofence.id}-${index}`,
            request,
            geoFence,
            type: 'request',
            legendKey: requestKey,
            legendName: request.administrationGroup,
            basePosition,
            color,
          });
        });

        return markers;
      }, []);
    },
    selectMarker(marker) {
      this.selectedMarker = marker;
    },
    toggleTask(driverKey, isChecked) {
      if (isChecked) {
        this.hiddenDriverKeys = this.hiddenDriverKeys.filter(key => key !== driverKey);
      } else if (!this.hiddenDriverKeys.includes(driverKey)) {
        this.hiddenDriverKeys = [...this.hiddenDriverKeys, driverKey];
      }

      this.closeSelectedMarkerIfHidden();
      this.updateSelectAllCheckboxState();
    },
    toggleAllTasks(isChecked) {
      const visibleDriverKeys = new Set(this.visibleDriverKeys);
      if (isChecked) {
        this.hiddenDriverKeys = this.hiddenDriverKeys.filter(driverKey => !visibleDriverKeys.has(driverKey));
      } else {
        this.hiddenDriverKeys = Array.from(new Set([...this.hiddenDriverKeys, ...this.visibleDriverKeys]));
      }

      this.closeSelectedMarkerIfHidden();
      this.updateSelectAllCheckboxState();
    },
    isTaskChecked(driverKey) {
      return !this.hiddenDriverKeys.includes(driverKey);
    },
    toggleRequest(requestKey, isChecked) {
      if (isChecked) {
        this.hiddenRequestKeys = this.hiddenRequestKeys.filter(key => key !== requestKey);
      } else if (!this.hiddenRequestKeys.includes(requestKey)) {
        this.hiddenRequestKeys = [...this.hiddenRequestKeys, requestKey];
      }

      this.closeSelectedMarkerIfHidden();
      this.updateSelectAllCheckboxState();
    },
    toggleAllRequests(isChecked) {
      const visibleRequestKeys = new Set(this.visibleRequestKeys);
      if (isChecked) {
        this.hiddenRequestKeys = this.hiddenRequestKeys.filter(requestKey => !visibleRequestKeys.has(requestKey));
      } else {
        this.hiddenRequestKeys = Array.from(new Set([...this.hiddenRequestKeys, ...this.visibleRequestKeys]));
      }

      this.closeSelectedMarkerIfHidden();
      this.updateSelectAllCheckboxState();
    },
    isRequestChecked(requestKey) {
      return !this.hiddenRequestKeys.includes(requestKey);
    },
    closeSelectedMarkerIfHidden() {
      if (!this.selectedMarker || (this.selectedMarker.type === 'task'
          ? this.activeDriverKeys.has(this.selectedMarker.legendKey)
          : this.activeRequestKeys.has(this.selectedMarker.legendKey))) {
        return;
      }

      this.selectedMarker = null;
    },
    createGeoFenceInfoWindowHeaderContent(driverName) {
      const header = document.createElement('div');
      header.className = 'map-info-window-header';
      header.textContent = driverName;
      return header;
    },
    updateSelectAllCheckboxState() {
      this.$nextTick(() => {
        const checkboxes = [
          [this.$refs.selectAllTasksCheckbox, this.isAllVisibleDriversIndeterminate],
          [this.$refs.selectAllRequestsCheckbox, this.isAllVisibleRequestsIndeterminate],
        ];
        checkboxes.forEach(([checkbox, isIndeterminate]) => {
          if (checkbox?.tagName === 'INPUT') {
            checkbox.indeterminate = isIndeterminate;
          }
        });
      });
    },
    fitMapToMarkers() {
      if (!this.isMapReady || this.geoFenceMarkers.length === 0) {
        return;
      }

      if (this.geoFenceMarkers.length === 1) {
        this.center = this.geoFenceMarkers[0].position;
        this.$refs.googleMapRef?.map?.setCenter(this.center);
        this.$refs.googleMapRef?.map?.setZoom(14);
        return;
      }

      const bounds = GoogleMapsManager.getMapBoundsByPositions(this.geoFenceMarkers.map(marker => marker.position));
      this.$refs.googleMapRef?.map?.fitBounds(bounds);
    },
    getTaskDriverKey(task) {
      return task?.driverId ? `driver-${task.driverId}` : 'driver-empty';
    },
    getTaskDriverName(task) {
      if (!task?.driverId) {
        return this.$t('oazis-suppliers-map.no-driver');
      }

      return entityDataManager.getById(this.drivers, task.driverId)?.name ?? this.$t('oazis-suppliers-map.no-driver');
    },
    getTaskVehicleName(task) {
      if (!task?.vehicleId) {
        return '-';
      }

      return entityDataManager.getById(this.vehicles, task.vehicleId)?.nickname ?? '-';
    },
    getRequestKey(request) {
      return `request-${request.administrationGroup}`;
    },
    legendMatchesSearch(legendItem) {
      if (!this.normalizedSearchToken) {
        return true;
      }

      return legendItem.name.toLowerCase().includes(this.normalizedSearchToken);
    },
    getLegendColor(index) {
      if (index < legendPalette.length) {
        return legendPalette[index];
      }

      const hue = (index * 137.508) % 360;
      return `hsl(${hue}, 68%, 44%)`;
    },
    getCoordinateKey(position) {
      return `${position.lat.toFixed(7)}:${position.lng.toFixed(7)}`;
    },
    offsetPosition(position, index, total) {
      if (total <= 1) {
        return position;
      }

      const radius = 0.000035 + Math.floor(index / 8) * 0.00002;
      const angle = (2 * Math.PI * index) / total;
      const lngMultiplier = 1 / Math.max(0.000001, Math.abs(Math.cos(position.lat * Math.PI / 180)));

      return {
        lat: position.lat + Math.cos(angle) * radius,
        lng: position.lng + Math.sin(angle) * radius * lngMultiplier,
      };
    },
    createPinOptions(color) {
      return {
        background: color,
        borderColor: '#202124',
        glyph: '',
      };
    },
  },
};
</script>

<style scoped lang="scss">
.map {
  width: 100%;
  height: calc(100vh - 105px);
  border-radius: 15px;
}

.suppliers-map-date,
.suppliers-map-legend {
  background: rgba(255, 255, 255, 0.96);
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(32, 33, 36, 0.2);
}

.suppliers-map-date {
  min-width: 180px;
  margin-top: 8px;
  padding: 2px;
}

.suppliers-map-legend {
  width: 230px;
  max-width: 230px;
  max-height: calc(100vh - 265px);
  margin-left: 10px;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: 5px 5px;
}

.suppliers-map-legend__item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 2px 0;
  font-size: 14px;
}

.suppliers-map-legend__item + .suppliers-map-legend__item {
  border-top: 1px solid rgba(32, 33, 36, 0.14);
}

.suppliers-map-legend__select-all + .suppliers-map-legend__select-all {
  border-top: 0;
}

.suppliers-map-legend__select-all {
  cursor: pointer;
  font-weight: 600;
}

.suppliers-map-legend__accordion-toggle {
  background: transparent;
  border: 0;
  color: inherit;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  justify-content: space-between;
  line-height: 18px;
  text-align: left;
  width: 100%;
}

.suppliers-map-legend__driver-toggle {
  cursor: pointer;
}

.suppliers-map-legend__select-all-label {
  line-height: 18px;
}

.suppliers-map-legend__checkbox {
  flex: 0 0 auto;
}

.suppliers-map-legend__checkbox-wrapper {
  flex: 0 0 auto;
  line-height: 0;
  position: relative;
}

.suppliers-map-legend__checkbox-wrapper .suppliers-map-legend__checkbox {
  margin-top: 0;
}

.suppliers-map-legend__checkbox-indeterminate {
  background: #202124;
  border-radius: 2px;
  height: 2px;
  left: 3px;
  pointer-events: none;
  position: absolute;
  right: 3px;
  top: 50%;
  transform: translateY(-50%);
}

.suppliers-map-legend__swatch {
  flex: 0 0 14px;
  width: 14px;
  height: 14px;
  border: 1px solid #202124;
  border-radius: 50%;
}

.suppliers-map-legend__text {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.suppliers-map-legend__driver,
.suppliers-map-legend__vehicle {
  overflow-wrap: anywhere;
}

.suppliers-map-legend__vehicle {
  color: #5f6368;
  font-size: 12px;
}

@media (max-width: 768px) {
  .suppliers-map-date {
    min-width: 150px;
  }

  .suppliers-map-legend {
    width: 210px;
    max-width: 210px;
  }
}
</style>
